/**
 * Build AdminMailOrderModal initial state from checkout cart + customer form.
 */

import { getLineUnitPriceCents } from './cartPricing';
import { getRetailPriceCents } from './wholesalePricing';
import { getStateProvinceValue, resolveCountryCode } from './addressUtils';
import { formatMailOrderShippingAddress } from './mailOrderAddress';

export function formatAddressForMailOrder(shippingAddress) {
  if (!shippingAddress) return '';
  if (typeof shippingAddress === 'string') return shippingAddress.trim();
  const countryCode = resolveCountryCode(shippingAddress);
  return formatMailOrderShippingAddress({
    street: shippingAddress.street,
    street2: shippingAddress.street2,
    city: shippingAddress.city,
    province: getStateProvinceValue(shippingAddress, countryCode),
    postalCode: shippingAddress.postalCode,
    countryCode,
  });
}

function detectPricingFromCartItems(items) {
  const productLines = (items || []).filter((item) => !item.isCheckoutPromo);
  if (!productLines.length) {
    return { pricingMode: 'retail', customerIsWholesaler: false };
  }

  for (const item of productLines) {
    const unit = getLineUnitPriceCents(item);
    const retail = getRetailPriceCents(item);
    if (item.isWholesalePrice || (retail > 0 && unit < retail)) {
      return { pricingMode: 'wholesale', customerIsWholesaler: true };
    }
  }

  return { pricingMode: 'retail', customerIsWholesaler: false };
}

export function buildMailOrderDraftFromCheckout({
  items,
  formData,
  shippingAddress,
  shipping,
  tax,
  customerIsWholesaler = false,
}) {
  const countryCode = resolveCountryCode(shippingAddress);
  const province = getStateProvinceValue(shippingAddress, countryCode);
  const pricing = detectPricingFromCartItems(items);

  const lines = (items || [])
    .filter((item) => !item.isCheckoutPromo)
    .map((item, index) => ({
      key: `checkout-${item.id}-${item.selectedSize || 'na'}-${item.selectedColor || 'na'}-${index}`,
      productId: String(item.id),
      quantity: Number(item.quantity) || 1,
      unitPriceCents: getLineUnitPriceCents(item),
    }));

  const useWholesale = customerIsWholesaler || pricing.customerIsWholesaler;

  return {
    customerName: [formData.shippingFirstName, formData.shippingLastName]
      .filter(Boolean)
      .join(' ')
      .trim(),
    customerEmail: formData.email?.trim() || '',
    customerPhone: formData.phone?.trim() || '',
    shippingStreet: shippingAddress.street || '',
    shippingStreet2: shippingAddress.street2 || '',
    shippingCity: shippingAddress.city || '',
    shippingPostalCode: shippingAddress.postalCode || '',
    shippingCountry: countryCode || 'CA',
    shippingProvince: province || '',
    customerIsWholesaler: useWholesale,
    pricingMode: useWholesale ? 'wholesale' : 'retail',
    lines,
    shippingCents: Number(shipping?.cost) || 0,
    taxCents: Number(tax?.amount) || 0,
    shippingOverridden: true,
    taxOverridden: true,
    notes: 'Created from admin shop checkout.',
  };
}

export function buildMailOrderDraftFromCartInquiry(inquiry) {
  const shippingAddress = inquiry?.shipping_address || {};
  const countryCode = resolveCountryCode(shippingAddress) || 'CA';
  const province = getStateProvinceValue(shippingAddress, countryCode);
  const items = inquiry?.items || [];
  const pricing = detectPricingFromCartItems(items);
  const useWholesale = pricing.customerIsWholesaler;
  const ref = inquiry?.reference_id || '';
  const message = String(inquiry?.personal_message || '').trim();

  return {
    customerName: inquiry?.customer_name || '',
    customerEmail: inquiry?.customer_email || '',
    customerPhone: inquiry?.customer_phone || '',
    shippingStreet: shippingAddress.street || '',
    shippingStreet2: shippingAddress.street2 || '',
    shippingCity: shippingAddress.city || '',
    shippingPostalCode: shippingAddress.postalCode || '',
    shippingCountry: countryCode,
    shippingProvince: province || '',
    linkedUserId: inquiry?.user_id > 0 ? inquiry.user_id : null,
    customerIsWholesaler: useWholesale,
    pricingMode: useWholesale ? 'wholesale' : 'retail',
    lines: items
      .filter((item) => !item.isCheckoutPromo)
      .map((item, index) => ({
        key: `inquiry-${inquiry?.id || 'x'}-${item.id}-${index}`,
        productId: String(item.id),
        quantity: Number(item.quantity) || 1,
        unitPriceCents: getLineUnitPriceCents(item),
      })),
    shippingCents: Number(inquiry?.shipping) || 0,
    taxCents: Number(inquiry?.tax) || 0,
    shippingOverridden: true,
    taxOverridden: true,
    paymentMethod: 'Check',
    includeCheckInstructions: true,
    notes: [
      ref ? `Created from cart inquiry ${ref}.` : 'Created from cart inquiry.',
      message ? `Customer message:\n${message}` : '',
    ].filter(Boolean).join('\n\n'),
  };
}
